import { unwrap, type SetupDraftIn, type WorkspaceOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, toast } from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { queryKeys } from "../../../../lib/query-keys";
import { GoalPicker } from "../../../../onboarding/goal-picker";
import { IndustryPicker } from "../../../../onboarding/industry-picker";
import { ProfileFields } from "../../../../onboarding/profile-fields";
import { useSetupChoices } from "../../../../onboarding/use-setup-choices";
import { useApi } from "../../../../session/session-provider";
import { CardActions } from "./panel-kit";
import { ResuggestDialog } from "./resuggest-dialog";

type Goal = NonNullable<SetupDraftIn["goals"]>[number];

/** Settings → Organization: industry (+ custom label), goals and follow-up answers stay editable. */
export function IndustryProfileCard({ workspace, canManage }: { workspace: WorkspaceOut; canManage: boolean }) {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  const choices = useSetupChoices(workspace);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const blocked = !choices.industry || (choices.industry === "other" && !choices.industryLabel.trim()) || choices.missing.length > 0;
  const save = async () => {
    setSaving(true);
    try {
      const body = { industry: choices.industry, industry_label: choices.industry === "other" ? choices.industryLabel.trim() : null, goals: choices.goals as Goal[], profile: choices.answers };
      const updated = await unwrap(api.PATCH("/v1/workspace", { body }));
      queryClient.setQueryData(queryKeys.workspace(workspace.id), updated);
      toast.success(t("settings.setup.saved"));
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="card" aria-labelledby="setup-title" data-testid="industry-profile-card">
      <div className="card-header">
        <div>
          <h2 id="setup-title" className="card-title">
            {t("settings.setup.title")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("settings.setup.subtitle")}</p>
        </div>
        <Button variant="secondary" size="sm" disabled={!canManage} onClick={() => { setOpen(true); }}>
          {t("settings.setup.resuggest")}
        </Button>
      </div>
      <fieldset className="card-body space-y-6" disabled={!canManage}>
        <IndustryPicker value={choices.industry} onChange={choices.setIndustry} label={choices.industryLabel} onLabelChange={choices.setIndustryLabel} />
        <GoalPicker goals={choices.goalsQuery.data} isPending={choices.goalsQuery.isPending} isError={choices.goalsQuery.isError} value={choices.goals} onChange={choices.setGoals} />
        <ProfileFields fields={choices.fields} answers={choices.answers} country={workspace.country} onChange={choices.setAnswers} missing={choices.missing} />
      </fieldset>
      <CardActions>
        <Button disabled={!canManage || blocked} loading={saving} onClick={() => void save()}>
          {t("settings.setup.save")}
        </Button>
      </CardActions>
      {open ? <ResuggestDialog workspace={workspace} open={open} onOpenChange={setOpen} /> : null}
    </section>
  );
}
